<script setup lang="ts">
import { ref, watch, reactive } from 'vue';
import { map, isObject, isString } from 'lodash';

interface Row {
  field: string;
  value: any;
  nested: boolean;
  isExpanded: boolean;
  truncate: boolean;
  tagName: string;
}

const props = withDefaults(
  defineProps<{
    json: object;
    truncateText?: number;
  }>(),
  {
    truncateText: 200,
  },
);

function getRows(): Row[] {
  return map(props.json, (value: any, field: string) => {
    const nested = isObject(value);
    const truncate =
      !nested &&
      isString(value) &&
      (value as string).length > props.truncateText;
    const hasLineBreaks =
      !nested && isString(value) && (value as string).includes('\n');

    return reactive<Row>({
      field,
      value: isString(value) ? (value as string).trim() : value,
      nested,
      isExpanded: false,
      truncate,
      tagName: hasLineBreaks ? 'pre' : 'span',
    });
  });
}

const rows = ref<Row[]>(getRows());

watch(
  () => props.json,
  () => {
    rows.value = getRows();
  },
  { deep: true },
);

function truncated(value?: string) {
  return value ? value.substring(0, props.truncateText) : value;
}
</script>

<template>
  <table class="table table-bordered table-striped table-sm">
    <tbody>
      <tr v-for="row in rows" :key="row.field">
        <td>{{ row.field }}</td>
        <td>
          <div v-if="!row.nested">
            <template v-if="!row.truncate">
              <component :is="row.tagName" class="m-0 p-0">{{
                row.value
              }}</component>
            </template>

            <template v-if="row.truncate">
              <component :is="row.tagName" class="m-0 p-0">{{
                row.isExpanded ? row.value : truncated(row.value)
              }}</component>

              <button
                class="btn btn-link"
                @click.prevent.stop="row.isExpanded = !row.isExpanded"
              >
                {{ row.isExpanded ? '&lt;&lt;' : '...' }}
              </button>
            </template>
          </div>

          <div v-else>
            <button
              class="btn btn-sm btn-outline-secondary"
              @click.prevent.stop="row.isExpanded = !row.isExpanded"
            >
              {{ row.isExpanded ? '-' : '+' }}
            </button>

            <ItpJSONToTable
              v-if="row.isExpanded"
              :truncate-text="truncateText"
              :json="row.value"
            />
          </div>
        </td>
      </tr>
    </tbody>
  </table>
</template>
